import { Link, useNavigate, useLocation } from "react-router-dom";
import { NavigationIcon } from "@/components/icons/NavigationIcon";
import { SiteMegaMenu } from "@/components/SiteMegaMenu";
import { Button } from "@/components/ui/button";
import everAfterLogo from "@/assets/ever-after-logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, Gem, User, LogOut, LayoutDashboard, MessageSquare, Menu, ChevronRight, ChevronLeft, Church, Globe, Monitor, ClipboardList, BookOpen, Calculator } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState, useEffect, lazy, Suspense } from "react";
import { Capacitor } from "@capacitor/core";
// Lazy-load NotificationBell - pulls date-fns + supabase realtime channel
// and only renders for authenticated users. Keeping it out of the eager
// chunk shrinks the JS payload for first-time/logged-out visitors.
const NotificationBell = lazy(() =>
  import("@/components/NotificationBell").then((m) => ({ default: m.NotificationBell }))
);
const menuItems = [{
  name: "Planning Tools",
  icon: ClipboardList,
  submenu: [{
    name: "My Wedding",
    path: "/couple/dashboard"
  }, {
    name: "Checklist",
    path: "/couple/checklist"
  }, {
    name: "Budget Planner",
    path: "/couple/budget"
  }, {
    name: "Guest List",
    path: "/couple/guests"
  }, {
    name: "Find Suppliers",
    path: "/couple/suppliers"
  }, {
    name: "Wedding Website",
    path: "/couple/website"
  }, {
    name: "Wedding Cost Estimator",
    path: "/couple/pricing-estimator"
  }]
}, {
  name: "Wedding Venues",
  icon: Church,
  submenu: [{
    name: "Affordable",
    path: "/directory?type=venues&style=affordable"
  }, {
    name: "Barn",
    path: "/directory?type=venues&style=barn"
  }, {
    name: "Castle",
    path: "/directory?type=venues&style=castle"
  }, {
    name: "Country House",
    path: "/directory?type=venues&style=country-house"
  }, {
    name: "Hotel",
    path: "/directory?type=venues&style=hotel"
  }, {
    name: "Intimate",
    path: "/directory?type=venues&style=intimate"
  }, {
    name: "Luxury",
    path: "/directory?type=venues&style=luxury"
  }, {
    name: "Outdoor",
    path: "/directory?type=venues&style=outdoor"
  }, {
    name: "Pub / Restaurant",
    path: "/directory?type=venues&style=pub"
  }, {
    name: "View All Wedding Venues",
    path: "/directory?type=venues",
    featured: true
  }]
}, {
  name: "Wedding Suppliers",
  icon: Globe,
  submenu: [{
    name: "Photographers",
    path: "/directory?category=photographers"
  }, {
    name: "Florists",
    path: "/directory?category=florists"
  }, {
    name: "Music",
    path: "/directory?category=music"
  }, {
    name: "Videographers",
    path: "/directory?category=videographers"
  }, {
    name: "Catering",
    path: "/directory?category=catering"
  }, {
    name: "Entertainment",
    path: "/directory?category=entertainment"
  }, {
    name: "Cakes",
    path: "/directory?category=cakes"
  }, {
    name: "Wedding Dresses",
    path: "/directory?category=dresses"
  }, {
    name: "Menswear",
    path: "/directory?category=menswear"
  }, {
    name: "Wedding Rings",
    path: "/directory?category=rings"
  }, {
    name: "Planners",
    path: "/directory?category=planners"
  }, {
    name: "Celebrants",
    path: "/directory?category=celebrants"
  }, {
    name: "Beauty, Hair & Makeup",
    path: "/directory?category=beauty"
  }, {
    name: "Stationery",
    path: "/directory?category=stationery"
  }, {
    name: "Transport",
    path: "/directory?category=transport"
  }, {
    name: "Decoration & Hire",
    path: "/directory?category=decoration"
  }, {
    name: "Marquee Hire",
    path: "/directory?category=marquee"
  }, {
    name: "View All Wedding Suppliers",
    path: "/directory",
    featured: true
  }]
}, {
  name: "Inspiration",
  icon: BookOpen,
  path: "/inspiration"
}, {
  name: "Wedding Cost Estimator",
  icon: Calculator,
  path: "/couple/pricing-estimator"
}, {
  name: "Wedding Websites",
  icon: Monitor,
  path: "/blog?category=websites"
}, {
  name: "Advice",
  icon: BookOpen,
  submenu: [{
    name: "General Advice",
    path: "/blog?category=general"
  }, {
    name: "Expert Advice",
    path: "/blog?category=expert"
  }, {
    name: "Wedding Budget",
    path: "/blog?category=budget"
  }, {
    name: "Guests",
    path: "/blog?category=guests"
  }, {
    name: "Ceremony",
    path: "/blog?category=ceremony"
  }, {
    name: "Post-Wedding",
    path: "/blog?category=post-wedding"
  }, {
    name: "Real Wedding",
    path: "/inspiration"
  }, {
    name: "View All Advice",
    path: "/blog",
    featured: true
  }]
}];
const businessMenu: { name: string; path?: string; submenu: { name: string; path: string; featured?: boolean }[] } = { name: 'For Businesses', submenu: [
          { name: 'List your business', path: '/business', featured: true },
          { name: 'Venue plans', path: '/business/venue/pricing' },
          { name: 'Supplier plans', path: '/business/pricing' },
          { name: 'Business login', path: '/business/login' },
          { name: 'Join as a venue', path: '/business/venue/signup' },
          { name: 'Join as a supplier', path: '/business/supplier/signup' },
        ] };
const mobileMenuItems = [...menuItems, businessMenu];
export const Navbar = () => {
  const {
    user,
    userRole,
    userRoles,
    isAdmin,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isApp, setIsApp] = useState(false);
  const location = useLocation();
  const isBusinessArea = location.pathname.startsWith('/supplier') || location.pathname.startsWith('/business') || location.pathname.startsWith('/venue');

  useEffect(() => {
    // Check if running in native app or installed PWA
    const isNative = Capacitor.isNativePlatform();
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    setIsApp(isNative || isPWA);
  }, []);
  const getDashboardLink = () => {
    if (userRole === 'super_admin' || userRole === 'admin') return '/admin';
    if (userRole === 'supplier') return '/supplier/dashboard';
    if (userRole === 'venue_owner') return '/venue/dashboard';
    if (userRole === 'couple') return '/couple/dashboard';
    return '/';
  };
  const getDashboardLinks = () => {
    const links: {
      label: string;
      path: string;
    }[] = [];
    if (userRoles.includes('super_admin') || userRoles.includes('admin')) {
      links.push({
        label: 'Admin Dashboard',
        path: '/admin'
      });
    }
    if (userRoles.includes('supplier')) {
      links.push({
        label: 'Supplier Dashboard',
        path: '/supplier/dashboard'
      });
    }
    if (userRoles.includes('venue_owner')) {
      links.push({
        label: 'Venue Dashboard',
        path: '/venue/dashboard'
      });
    }
    if (userRoles.includes('couple')) {
      links.push({
        label: 'Couple Dashboard',
        path: '/couple/dashboard'
      });
    }
    return links;
  };
  // Hide navbar completely in app
  if (isApp) return null;

  return <nav className="border-b bg-card sticky top-0 z-50" style={{ 
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)'
    }}>
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between px-2 xl:h-24">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={everAfterLogo} 
              alt="Ever After List" 
              className="h-12 xl:h-16 w-auto object-contain dark:invert"
            />
          </Link>

          {/* Desktop Menu - only on xl+ so tablets (incl. iPad landscape) get the burger menu */}
          <div className="hidden xl:flex items-center gap-4 xl:gap-6">
            {user ? (
              <>
                <Suspense fallback={null}><NotificationBell /></Suspense>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      <User className="h-4 w-4" />
                      My Account
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {getDashboardLinks().length > 1 ? (
                      <>
                        {getDashboardLinks().map(link => (
                          <DropdownMenuItem key={link.path} onClick={() => navigate(link.path)}>
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            {link.label}
                          </DropdownMenuItem>
                        ))}
                      </>
                    ) : (
                      <DropdownMenuItem onClick={() => navigate(getDashboardLink())}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin/suppliers')}>
                          <User className="mr-2 h-4 w-4" />
                          Manage Suppliers
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                          <User className="mr-2 h-4 w-4" />
                          Manage Users
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/messages')}>
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={signOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/auth?role=couple">
                  <Button variant="ghost" size="sm">Login</Button>
                </Link>
                <Link to="/auth?role=couple&mode=signup">
                  <Button size="sm" className="bg-primary hover:bg-primary/90">Get Started</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="xl:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[400px] p-0 bg-background !opacity-100 backdrop-blur-none overflow-hidden" style={{ paddingTop: "var(--safe-area-inset-top)", backgroundColor: "hsl(var(--tint-cream))" }}>
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <SheetDescription className="sr-only">Site pages and your account</SheetDescription>
              <div className="flex flex-col h-full overflow-hidden relative">
                {/* Main Menu */}
                <div className={`flex-1 overflow-y-auto transition-transform duration-300 ${activeSubmenu ? '-translate-x-full invisible' : 'translate-x-0'}`}>
                  <div className="px-5 pb-5 pt-12"><p className="mb-2 px-3 text-xs font-semibold uppercase tracking-widest text-primary">Your wedding starts here</p><h2 className="mb-5 px-3 font-sans text-2xl font-bold tracking-tight">Make it yours.</h2>
                    {mobileMenuItems.map(item => <button key={item.name} onClick={() => {
                    if (item.submenu) {
                      setActiveSubmenu(item.name);
                    } else if (item.path) {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }
                  }} className="flex items-center justify-between w-full py-4 text-left hover:bg-primary/10 transition-colors border-b border-border/50 px-3">
                        <div className="flex items-center gap-3">
                          <NavigationIcon name={item.name} />
                          <span className="text-base font-semibold">{item.name}</span>
                        </div>
                        {item.submenu && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                      </button>)}
                  </div>

                  {/* Bottom Login Buttons */}
                  <div className="border-t p-6 space-y-3">
                    {user ? <>
                        {getDashboardLinks().length > 1 ? <>
                            {getDashboardLinks().map(link => <Button key={link.path} variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                        navigate(link.path);
                        setMobileMenuOpen(false);
                      }}>
                                <LayoutDashboard className="h-4 w-4" />
                                {link.label}
                              </Button>)}
                          </> : <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                      navigate(getDashboardLink());
                      setMobileMenuOpen(false);
                    }}>
                            <LayoutDashboard className="h-4 w-4" />
                            Dashboard
                          </Button>}
                        {isAdmin && <>
                            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                        navigate('/admin/suppliers');
                        setMobileMenuOpen(false);
                      }}>
                              <User className="h-4 w-4" />
                              Manage Suppliers
                            </Button>
                            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                        navigate('/admin/users');
                        setMobileMenuOpen(false);
                      }}>
                              <User className="h-4 w-4" />
                              Manage Users
                            </Button>
                          </>}
                        <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                      navigate('/messages');
                      setMobileMenuOpen(false);
                    }}>
                          <MessageSquare className="h-4 w-4" />
                          Messages
                        </Button>
                        <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => {
                      signOut();
                      setMobileMenuOpen(false);
                    }}>
                          <LogOut className="h-4 w-4" />
                          Sign Out
                        </Button>
                      </> : <div className="flex gap-3">
                        <Link to="/auth?role=couple" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                          <Button variant="outline" className="w-full">Log in</Button>
                        </Link>
                        <Link to="/business" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                          <Button variant="outline" className="w-full">Business login</Button>
                        </Link>
                      </div>}
                  </div>
                </div>

                {/* Submenu */}
                {activeSubmenu && <div
                  className="absolute inset-0 z-10 flex flex-col bg-background"
                  style={{
                    backgroundColor: "hsl(var(--tint-cream))",
                    backgroundImage: "none",
                    backdropFilter: "none",
                    WebkitBackdropFilter: "none",
                    opacity: 1,
                  }}
                >
                    <div
                      className="border-b p-4 flex items-center gap-3 justify-between bg-background"
                      style={{ paddingTop: "var(--safe-area-inset-top)", backgroundColor: "hsl(var(--tint-cream))" }}
                    >
                      <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => setActiveSubmenu(null)}>
                          <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <h2 className="font-sans text-lg font-bold">{activeSubmenu}</h2>
                      </div>
                      <Button variant="ghost" size="icon" aria-label="Close menu" onClick={() => {
                        setMobileMenuOpen(false);
                        setActiveSubmenu(null);
                      }}>
                        <span className="text-2xl">&times;</span>
                      </Button>
                    </div>
                    <div
                      className="flex-1 overflow-y-auto p-6 space-y-1 bg-background"
                      style={{ backgroundColor: "hsl(var(--tint-cream))" }}
                    >
                      {mobileMenuItems.find(item => item.name === activeSubmenu)?.submenu?.map(subItem => <Link key={subItem.name} to={subItem.path} onClick={() => {
                    setMobileMenuOpen(false);
                    setActiveSubmenu(null);
                  }} className={`flex items-center gap-4 py-4 text-base hover:bg-primary/10 transition-colors border-b border-border/50 px-2 ${subItem.featured ? 'text-primary font-medium' : ''}`}>
                            <NavigationIcon name={subItem.name} /><span>{subItem.name}</span>
                          </Link>)}
                    </div>
                  </div>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <SiteMegaMenu groups={[
        ...menuItems.filter(item => !['Wedding Cost Estimator', 'Wedding Websites'].includes(item.name)),
        businessMenu,
      ]} />
    </nav>;
};